import React, {
    useState,
    useEffect,
    useMemo,
    useReducer,
    useContext,
    useRef,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles"; // Certifique-se que 'useTheme' está importado
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import BusinessIcon from "@material-ui/icons/Business";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Checkbox from "@material-ui/core/Checkbox"; // Importar Checkbox
import Chip from "@material-ui/core/Chip";
import Badge from "@material-ui/core/Badge";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import BlockIcon from "@material-ui/icons/Block";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import DuplicateContactsModal from "../../components/DuplicateContactsModal";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import formatSerializedId from '../../utils/formatSerializedId';
import { v4 as uuidv4 } from "uuid";

import {
    ArrowDropDown,
    Backup,
    ContactPhone,
} from "@material-ui/icons";
import { Menu, MenuItem, Dialog, DialogTitle, DialogContent, CircularProgress } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import NoteAddIcon from "@material-ui/icons/NoteAdd";

import ContactImportWpModal from "../../components/ContactImportWpModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

// Contato sem nome de verdade (numero cru, LID, so simbolos) - mostra
// um rotulo neutro em vez do lixo, mas mantem o valor real no title
// pra quem precisar conferir/debugar.
const isUnidentifiedContactName = (name) => !name || !/[a-zA-ZÀ-ÿ]/.test(name);

const reducer = (state, action) => {
    if (action.type === "LOAD_CONTACTS") {
        const contacts = action.payload;
        const newContacts = [];

        contacts.forEach((contact) => {
            const contactIndex = state.findIndex((c) => c.id === contact.id);
            if (contactIndex !== -1) {
                state[contactIndex] = contact;
            } else {
                newContacts.push(contact);
            }
        });

        return [...state, ...newContacts];
    }

    if (action.type === "UPDATE_CONTACTS") {
        const contact = action.payload;
        const contactIndex = state.findIndex((c) => c.id === contact.id);

        if (contactIndex !== -1) {
            state[contactIndex] = contact;
            return [...state];
        } else {
            return [contact, ...state];
        }
    }

    if (action.type === "DELETE_CONTACT") {
        const contactId = action.payload;

        const contactIndex = state.findIndex((c) => c.id === contactId);
        if (contactIndex !== -1) {
            state.splice(contactIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
}));

const Contacts = () => {
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme(); // Adicione esta linha para acessar o tema
    // Tabela de 9 colunas sem overflow-x vazava da tela em celular (achado
    // real da auditoria mobile 2026-09-19) - abaixo do breakpoint "sm"
    // troca pra lista de cards por empresa (mesmo agrupamento por CNPJ
    // de cima, so' que pensado pro dedo em vez de linha de tabela).
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchParam, setSearchParam] = useState("");
    const [contacts, dispatch] = useReducer(reducer, []);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [duplicatesModalOpen, setDuplicatesModalOpen] = useState(false);
    const [duplicateCandidatesCount, setDuplicateCandidatesCount] = useState(0);

    // Vincular/criar contato direto na linha da empresa (Direção 1)
    const [targetOrgForLink, setTargetOrgForLink] = useState(null);
    const [linkMenuAnchor, setLinkMenuAnchor] = useState(null);
    const [linkExistingDialogOpen, setLinkExistingDialogOpen] = useState(false);
    const [linkSearchTerm, setLinkSearchTerm] = useState("");
    const [linkSearchOptions, setLinkSearchOptions] = useState([]);
    const [linkSearchLoading, setLinkSearchLoading] = useState(false);
    const [linkingContact, setLinkingContact] = useState(false);
    const [newContactInitialValues, setNewContactInitialValues] = useState(null);

    const [importContactModalOpen, setImportContactModalOpen] = useState(false);
    const [deletingContact, setDeletingContact] = useState(null);
    const [ImportContacts, setImportContacts] = useState(null);
    
    const [blockingContact, setBlockingContact] = useState(null);
    const [unBlockingContact, setUnBlockingContact] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [exportContact, setExportContact] = useState(false);
    const [confirmChatsOpen, setConfirmChatsOpen] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
    const [contactTicket, setContactTicket] = useState({});
    const fileUploadRef = useRef(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const { setCurrentTicket } = useContext(TicketsContext);

    const [importWhatsappId, setImportWhatsappId] = useState()

    // NOVOS ESTADOS PARA SELEÇÃO E DELEÇÃO EM MASSA
    const [selectedContactIds, setSelectedContactIds] = useState([]); // Array de IDs dos contatos selecionados
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false); // Estado para o checkbox "Selecionar Tudo"
    const [confirmDeleteManyOpen, setConfirmDeleteManyOpen] = useState(false); // Estado para o modal de confirmação de deleção em massa

    const { getAll: getAllSettings } = useCompanySettings();
    const [hideNum, setHideNum] = useState(false);
    const [enableLGPD, setEnableLGPD] = useState(false);

    // Agrupamento por CNPJ (ContactOrganization) - pedido do usuario
    // 2026-09-18: "cadastro de contatos precisa ser por CNPJ... a razao
    // social precisa ser a forma de identificarmos os clientes". Um
    // mesmo CNPJ pode ter varios numeros de telefone/contatos vinculados
    // (cada pessoa que liga da mesma empresa) - agrupa por
    // contactOrganization.id, mostrando razao social/CNPJ/fantasia como
    // linha principal e os telefones aparecem so' ao expandir. Contatos
    // sem organizacao vinculada (CNPJ nao informado ainda) continuam
    // aparecendo soltos, como antes.
    const [expandedOrgIds, setExpandedOrgIds] = useState(() => new Set());

    const toggleOrgExpanded = (orgId) => {
        setExpandedOrgIds((prev) => {
            const next = new Set(prev);
            if (next.has(orgId)) next.delete(orgId);
            else next.add(orgId);
            return next;
        });
    };

    // Badge no botao "Possíveis Duplicados" (pedido do usuario
    // 2026-11-03: a lista de candidatos ja existia mas so' quem lembrava
    // de clicar no botao ficava sabendo que havia algo pendente). Busca
    // a contagem uma vez ao entrar na tela; o modal mantem o numero
    // atualizado depois (onCountChange) conforme o usuario ignora/mescla.
    useEffect(() => {
        api
            .get("/contact-duplicate-candidates")
            .then(({ data }) => setDuplicateCandidatesCount((data.candidates || []).length))
            .catch(() => {});
    }, []);

    const formatCnpjCpf = (raw) => {
        const digits = (raw || "").replace(/\D/g, "");
        if (digits.length === 14) {
            return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
        }
        if (digits.length === 11) {
            return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
        }
        return raw || "-";
    };

    // Pedido do usuario 2026-10-06: organizacoes com contrato importado
    // da planilha do ERP de origem mas sem NENHUM contato de WhatsApp
    // vinculado ainda (a maioria dos clientes importados) ficavam
    // totalmente invisiveis nesta tela, pois antes so apareciam aqui
    // organizacoes derivadas dos contatos ja carregados.
    const [orgsWithContracts, setOrgsWithContracts] = useState([]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            const fetchOrgsWithContracts = async () => {
                try {
                    const { data } = await api.get("/contact-organizations/with-contracts", {
                        params: { searchParam },
                    });
                    setOrgsWithContracts(data);
                } catch (err) {
                    // Nao bloqueia a lista principal de contatos em caso de erro aqui
                }
            };
            fetchOrgsWithContracts();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam]);

    const { orgGroups, ungroupedContacts } = useMemo(() => {
        const groups = new Map();
        const ungrouped = [];
        contacts.forEach((contact) => {
            const org = contact.contactOrganization;
            if (org && org.id) {
                if (!groups.has(org.id)) {
                    groups.set(org.id, { org, contacts: [] });
                }
                groups.get(org.id).contacts.push(contact);
            } else {
                ungrouped.push(contact);
            }
        });
        orgsWithContracts.forEach((org) => {
            if (!groups.has(org.id)) {
                groups.set(org.id, { org, contacts: [] });
            }
        });
        const sortedGroups = Array.from(groups.values()).sort((a, b) =>
            (a.org.corporateName || a.org.tradeName || "").localeCompare(
                b.org.corporateName || b.org.tradeName || ""
            )
        );
        return { orgGroups: sortedGroups, ungroupedContacts: ungrouped };
    }, [contacts, orgsWithContracts]);

    useEffect(() => {
        async function fetchData() {
            const settingList = await getAllSettings(user.companyId);
            for (const [key, value] of Object.entries(settingList)) {
                if (key === "enableLGPD") setEnableLGPD(value === "enabled");
                if (key === "lgpdHideNumber") setHideNum(value === "enabled");
            }
        }
        fetchData();
    }, []);

    const handleImportExcel = async () => {
        try {
            const formData = new FormData();
            formData.append("file", fileUploadRef.current.files[0]);
            await api.request({
                url: `/contacts/upload`,
                method: "POST",
                data: formData,
            });
            history.go(0);
        } catch (err) {
            toastError(err);
        }
    };

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
        setSelectedContactIds([]); // Limpar seleção ao mudar filtro/pesquisa
        setIsSelectAllChecked(false); // Desmarcar "Selecionar Tudo"
    }, [searchParam, selectedTags]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchContacts = async () => {
                try {
                    const { data } = await api.get("/contacts/", {
                        params: { searchParam, pageNumber, contactTag: JSON.stringify(selectedTags) },
                    });
                    dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
                    setHasMore(data.hasMore);
                    setLoading(false);

                    // Atualizar o estado do "Selecionar Tudo" baseado nos contatos carregados e selecionados
                    const allCurrentContactIds = data.contacts.map(c => c.id);
                    const newSelected = selectedContactIds.filter(id => allCurrentContactIds.includes(id));
                    setSelectedContactIds(newSelected); // Mantenha apenas os IDs que ainda estão na lista
                    setIsSelectAllChecked(newSelected.length === allCurrentContactIds.length && allCurrentContactIds.length > 0);

                } catch (err) {
                    toastError(err);
                }
            };
            fetchContacts();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, selectedTags]);

    useEffect(() => {
        const companyId = user.companyId;
        const onContactEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
                // Remover o contato deletado da lista de selecionados, se estiver lá
                setSelectedContactIds((prevSelected) =>
                    prevSelected.filter((id) => id !== +data.contactId) // Use +data.contactId para garantir que seja número
                );
            }
        };
        socket.on(`company-${companyId}-contact`, onContactEvent);

        return () => {
            socket.off(`company-${companyId}-contact`, onContactEvent);
        };
    }, [socket]);

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    }

    const handleCloseOrOpenTicket = (ticket) => {
        setNewTicketModalOpen(false);
        if (ticket !== undefined && ticket.uuid !== undefined) {
            handleSelectTicket(ticket);
            history.push(`/tickets/${ticket.uuid}`);
        }
    };

    const handleSelectedTags = (selecteds) => {
        const tags = selecteds.map((t) => t.id);
        setSelectedTags(tags);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleOpenContactModal = () => {
        setSelectedContactId(null);
        setNewContactInitialValues(null);
        setContactModalOpen(true);
    };

    const handleCloseContactModal = () => {
        setSelectedContactId(null);
        setContactModalOpen(false);
        setNewContactInitialValues(null);
        setTargetOrgForLink(null);
    };

    // Pedido do usuario 2026-10-07 (Direção 1 dos mockups): botão "+" em
    // cada linha de empresa, com menu rápido pra vincular um contato já
    // existente ou criar um novo já associado a essa empresa - sem
    // precisar abrir o contato e digitar o CNPJ na mão.
    const handleOpenLinkMenu = (event, org) => {
        event.stopPropagation();
        setTargetOrgForLink(org);
        setLinkMenuAnchor(event.currentTarget);
    };

    const handleCloseLinkMenu = () => {
        setLinkMenuAnchor(null);
    };

    const handlePickCreateNew = () => {
        setNewContactInitialValues({
            document: targetOrgForLink.documentNumber,
            system: targetOrgForLink.system || "",
            domain: targetOrgForLink.domain || "",
        });
        setSelectedContactId(null);
        setContactModalOpen(true);
        setLinkMenuAnchor(null);
    };

    const handlePickLinkExisting = () => {
        setLinkExistingDialogOpen(true);
        setLinkMenuAnchor(null);
    };

    const handleCloseLinkExistingDialog = () => {
        setLinkExistingDialogOpen(false);
        setLinkSearchTerm("");
        setLinkSearchOptions([]);
        setTargetOrgForLink(null);
    };

    useEffect(() => {
        if (!linkExistingDialogOpen) return;
        if (!linkSearchTerm || linkSearchTerm.length < 2) {
            setLinkSearchOptions([]);
            return;
        }
        let active = true;
        setLinkSearchLoading(true);
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get("/contacts/list", { params: { name: linkSearchTerm } });
                if (active) setLinkSearchOptions(data || []);
            } catch (err) {
                toastError(err);
            } finally {
                if (active) setLinkSearchLoading(false);
            }
        }, 400);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [linkSearchTerm, linkExistingDialogOpen]);

    const handleLinkExistingContact = async (pickedContact) => {
        if (!pickedContact || !targetOrgForLink) return;
        setLinkingContact(true);
        try {
            await api.put(`/contacts/${pickedContact.id}/document`, {
                documentNumber: targetOrgForLink.documentNumber,
                corporateName: targetOrgForLink.corporateName,
                tradeName: targetOrgForLink.tradeName,
            });
            toast.success("Contato vinculado à empresa!");
            handleCloseLinkExistingDialog();
            dispatch({ type: "RESET" });
            setPageNumber(1);
        } catch (err) {
            toastError(err);
        } finally {
            setLinkingContact(false);
        }
    };

    const hadleEditContact = (contactId) => {
        setSelectedContactId(contactId);
        setContactModalOpen(true);
    };

    const handleDeleteContact = async (contactId) => {
        try {
            await api.delete(`/contacts/${contactId}`);
            toast.success(i18n.t("contacts.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
    };

    // NOVA FUNÇÃO: SELECIONAR UM CONTATO INDIVIDUALMENTE
    const handleToggleSelectContact = (contactId) => (event) => {
        if (event.target.checked) {
            setSelectedContactIds((prevSelected) => [...prevSelected, contactId]);
        } else {
            setSelectedContactIds((prevSelected) => prevSelected.filter((id) => id !== contactId));
            setIsSelectAllChecked(false); // Se um individual é desmarcado, "Selecionar Tudo" deve ser desmarcado
        }
    };

    // NOVA FUNÇÃO: SELECIONAR/DESSELECIONAR TODOS OS CONTATOS
    const handleSelectAllContacts = (event) => {
        const checked = event.target.checked;
        setIsSelectAllChecked(checked);

        if (checked) {
            // Seleciona todos os IDs dos contatos atualmente carregados
            const allContactIds = contacts.map((contact) => contact.id);
            setSelectedContactIds(allContactIds);
        } else {
            setSelectedContactIds([]);
        }
    };

    // Menu de ações por contato na lista mobile - as mesmas 4 ações que
    // no desktop viram icones soltos na linha (novo ticket/editar/
    // bloquear/excluir), mas num card estreito isso nao cabe, entao vira
    // um menu "..." em vez de 4 botoes lado a lado.
    const renderMobileContactActions = (contact) => (
        <PopupState variant="popover" popupId={`contact-actions-${contact.id}`}>
            {(popupState) => (
                <>
                    <IconButton size="small" {...bindTrigger(popupState)}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu {...bindMenu(popupState)}>
                        <MenuItem
                            disabled={!contact.active}
                            onClick={() => { popupState.close(); setContactTicket(contact); setNewTicketModalOpen(true); }}
                        >
                            Novo ticket
                        </MenuItem>
                        <MenuItem onClick={() => { popupState.close(); hadleEditContact(contact.id); }}>
                            Editar
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                popupState.close();
                                setConfirmOpen(true);
                                if (contact.active) setBlockingContact(contact);
                                else setUnBlockingContact(contact);
                            }}
                        >
                            {contact.active ? "Bloquear" : "Desbloquear"}
                        </MenuItem>
                        <Can
                            role={user.profile}
                            perform="contacts-page:deleteContact"
                            yes={() => (
                                <MenuItem onClick={() => { popupState.close(); setConfirmOpen(true); setDeletingContact(contact); }}>
                                    Excluir
                                </MenuItem>
                            )}
                        />
                    </Menu>
                </>
            )}
        </PopupState>
    );

    const renderMobileContactLine = (contact) => {
        const displayNumber = ((enableLGPD && hideNum && user.profile === "user")
            ? contact.isGroup
                ? contact.number :
                formatSerializedId(contact?.number) === null ? contact.number.slice(0, -6) + "**-**" + contact?.number.slice(-2) :
                    formatSerializedId(contact?.number)?.slice(0, -6) + "**-**" + contact?.number?.slice(-2) :
                    contact.isGroup ? contact.number : formatSerializedId(contact?.number)
        );
        return (
            <Box
                key={contact.id}
                display="flex"
                alignItems="center"
                style={{ gap: 8, padding: theme.spacing(1, 1.5), borderTop: `1px solid ${theme.palette.divider}` }}
            >
                <Checkbox
                    size="small"
                    checked={selectedContactIds.includes(contact.id)}
                    onChange={handleToggleSelectContact(contact.id)}
                    style={{ padding: 4 }}
                />
                <Avatar src={`${contact?.urlPicture}`} style={{ width: 32, height: 32 }} />
                <Box flex={1} minWidth={0}>
                    {isUnidentifiedContactName(contact.name) ? (
                        <Typography variant="body2" noWrap style={{ fontStyle: "italic", color: "#9e9e9e" }}>
                            Sem identificação
                        </Typography>
                    ) : (
                        <Typography variant="body2" noWrap style={{ fontWeight: 600 }}>
                            {contact.name}
                        </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">{displayNumber}</Typography>
                </Box>
                {contact.active ? (
                    <CheckCircleIcon style={{ color: "#1f8a5f" }} fontSize="small" />
                ) : (
                    <CancelIcon style={{ color: "#c1443a" }} fontSize="small" />
                )}
                {renderMobileContactActions(contact)}
            </Box>
        );
    };

    // Pedido do usuario 2026-10-06: "coloque essas informações no
    // cadastro de cliente... o sistema o dominio e os modulos... de
    // uma forma que fique facil analisar" - um chip por contrato ativo
    // (um cliente pode ter mais de um sistema), dominio junto no
    // mesmo label pra nao precisar passar o mouse pra ver o essencial;
    // modulos contratados ficam no title (tooltip nativo) pra nao
    // poluir a linha com o que normalmente e' so' detalhe de apoio.
    const renderOrgContracts = (org) => {
        if (!org.contracts || org.contracts.length === 0) return null;
        return (
            <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 4 }}>
                {org.contracts.map((c) => {
                    const modulosText = (c.modulos || [])
                        .map((m) => (m.quantidade ? `${m.nome} (${m.quantidade})` : m.nome))
                        .join(", ");
                    const title = ["Sistema: " + c.sistemaNome, modulosText && "Módulos: " + modulosText]
                        .filter(Boolean)
                        .join(" · ");
                    return (
                        <Chip
                            key={c.id}
                            size="small"
                            variant="outlined"
                            style={{ height: 20, fontSize: 11 }}
                            label={c.dominio ? `${c.sistemaNome} · ${c.dominio}` : c.sistemaNome}
                            title={title}
                        />
                    );
                })}
            </Box>
        );
    };

    const renderMobileOrgCard = (org, orgContacts) => {
        const isExpanded = expandedOrgIds.has(org.id);
        return (
            <Paper key={org.id} variant="outlined" style={{ marginBottom: theme.spacing(1) }}>
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: 10, padding: theme.spacing(1.25, 1.5), cursor: "pointer" }}
                    onClick={() => toggleOrgExpanded(org.id)}
                >
                    <BusinessIcon fontSize="small" color="disabled" />
                    <Box flex={1} minWidth={0}>
                        <Typography variant="body2" style={{ fontWeight: 700 }} noWrap>
                            {org.corporateName || org.tradeName || "(sem razão social)"}
                        </Typography>
                        {org.tradeName && org.tradeName !== org.corporateName && (
                            <Typography variant="caption" color="textSecondary" noWrap display="block">
                                {org.tradeName}
                            </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                            {formatCnpjCpf(org.documentNumber)}
                        </Typography>
                        {renderOrgContracts(org)}
                    </Box>
                    <Chip size="small" variant="outlined" label={`${orgContacts.length} tel.`} />
                    <IconButton size="small" onClick={(e) => handleOpenLinkMenu(e, org)} title="Adicionar contato a esta empresa">
                        <AddCircleOutlineIcon fontSize="small" color="primary" />
                    </IconButton>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
                {isExpanded && orgContacts.map((contact) => renderMobileContactLine(contact))}
            </Paper>
        );
    };

    // NOVA FUNÇÃO: DELETAR CONTATOS SELECIONADOS EM MASSA
    const handleDeleteSelectedContacts = async () => {
        try {
            setLoading(true);
            await api.delete("/contacts/batch-delete", {
                data: { contactIds: selectedContactIds } // Envia os IDs no corpo da requisição DELETE
            });
            toast.success("Contatos selecionados deletados com sucesso!");
            setSelectedContactIds([]); // Limpa a seleção
            setIsSelectAllChecked(false); // Desmarca o "Selecionar Tudo"
            setConfirmDeleteManyOpen(false); // Fecha o modal de confirmação
            // Re-fetch os contatos para atualizar a lista
            dispatch({ type: "RESET" });
            setPageNumber(1);
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };


    const handleBlockContact = async (contactId) => {
        try {
            await api.put(`/contacts/block/${contactId}`, { active: false });
            toast.success("Contato bloqueado");
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
        setSearchParam("");
        setPageNumber(1);
        setBlockingContact(null);
    };

    const handleUnBlockContact = async (contactId) => {
        try {
            await api.put(`/contacts/block/${contactId}`, { active: true });
            toast.success("Contato desbloqueado");
        } catch (err) {
            toastError(err);
        }
        setDeletingContact(null);
        setSearchParam("");
        setPageNumber(1);
        setUnBlockingContact(null);
    };

    const onSave = (whatsappId) => {
        setImportWhatsappId(whatsappId)
    }

    const handleimportContact = async () => {
        setImportContactModalOpen(false)

        try {
            await api.post("/contacts/import", { whatsappId: importWhatsappId });
            history.go(0);
            setImportContactModalOpen(false);
        } catch (err) {
            toastError(err);
            setImportContactModalOpen(false);
        }
    };

    const handleimportChats = async () => {
        console.log("handleimportChats")
        try {
            await api.post("/contacts/import/chats");
            history.go(0);
        } catch (err) {
            toastError(err);
        }
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    return (
        <MainContainer className={classes.mainContainer}>
            <NewTicketModal
                modalOpen={newTicketModalOpen}
                initialContact={contactTicket}
                onClose={(ticket) => {
                    handleCloseOrOpenTicket(ticket);
                }}
            />
            <ContactModal
                open={contactModalOpen}
                onClose={handleCloseContactModal}
                aria-labelledby="form-dialog-title"
                contactId={selectedContactId}
                initialValues={newContactInitialValues}
            ></ContactModal>

            <Menu
                anchorEl={linkMenuAnchor}
                open={Boolean(linkMenuAnchor)}
                onClose={handleCloseLinkMenu}
            >
                <MenuItem onClick={handlePickLinkExisting}>
                    <PersonAddIcon fontSize="small" style={{ marginRight: 8 }} />
                    Vincular contato existente
                </MenuItem>
                <MenuItem onClick={handlePickCreateNew}>
                    <NoteAddIcon fontSize="small" style={{ marginRight: 8 }} />
                    Criar novo contato aqui
                </MenuItem>
            </Menu>

            <Dialog open={linkExistingDialogOpen} onClose={handleCloseLinkExistingDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Vincular contato a {targetOrgForLink?.corporateName || targetOrgForLink?.tradeName || "esta empresa"}
                </DialogTitle>
                <DialogContent style={{ paddingBottom: 24 }}>
                    <Autocomplete
                        options={linkSearchOptions}
                        getOptionLabel={(option) => option.name ? `${option.name} · ${formatSerializedId(option.number)}` : ""}
                        loading={linkSearchLoading}
                        onInputChange={(event, value) => setLinkSearchTerm(value)}
                        onChange={(event, value) => handleLinkExistingContact(value)}
                        noOptionsText={linkSearchTerm.length < 2 ? "Digite ao menos 2 letras" : "Nenhum contato encontrado"}
                        loadingText="Buscando..."
                        disabled={linkingContact}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                autoFocus
                                variant="outlined"
                                margin="dense"
                                label="Buscar contato por nome"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {(linkSearchLoading || linkingContact) && <CircularProgress color="inherit" size={18} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </DialogContent>
            </Dialog>

            <DuplicateContactsModal
                open={duplicatesModalOpen}
                onClose={() => setDuplicatesModalOpen(false)}
                onCountChange={setDuplicateCandidatesCount}
            />
            
            <ConfirmationModal
                title={
                    deletingContact
                        ? `${i18n.t(
                            "contacts.confirmationModal.deleteTitle"
                        )} ${deletingContact.name}?`
                        : blockingContact
                            ? `Bloquear Contato ${blockingContact.name}?`
                            : unBlockingContact
                                ? `Desbloquear Contato ${unBlockingContact.name}?`
                                : ImportContacts
                                    ? `${i18n.t("contacts.confirmationModal.importTitlte")}`
                                    : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
                }
                onSave={onSave}
                isCellPhone={ImportContacts}
                open={confirmOpen}
                onClose={setConfirmOpen}
                onConfirm={(e) =>
                    deletingContact
                        ? handleDeleteContact(deletingContact.id)
                        : blockingContact
                            ? handleBlockContact(blockingContact.id)
                            : unBlockingContact
                                ? handleUnBlockContact(unBlockingContact.id)
                                : ImportContacts
                                    ? handleimportContact()
                                    : handleImportExcel()
                }
            >
                {exportContact
                    ?
                    `${i18n.t("contacts.confirmationModal.exportContact")}`
                    : deletingContact
                        ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
                        : blockingContact
                            ? `${i18n.t("contacts.confirmationModal.blockContact")}`
                            : unBlockingContact
                                ? `${i18n.t("contacts.confirmationModal.unblockContact")}`
                                : ImportContacts
                                    ? `Escolha de qual conexão deseja importar`
                                    : `${i18n.t(
                                        "contactListItems.confirmationModal.importMessage"
                                    )}`}
            </ConfirmationModal>

            {/* NOVO MODAL DE CONFIRMAÇÃO PARA DELEÇÃO EM MASSA */}
            <ConfirmationModal
                title={`Tem certeza que deseja deletar ${selectedContactIds.length} contatos selecionados?`}
                open={confirmDeleteManyOpen}
                onClose={() => setConfirmDeleteManyOpen(false)}
                onConfirm={handleDeleteSelectedContacts}
            >
                Essa ação é irreversível.
            </ConfirmationModal>

            <ConfirmationModal
                title={i18n.t("contacts.confirmationModal.importChat")}
                open={confirmChatsOpen}
                onClose={setConfirmChatsOpen}
                onConfirm={(e) => handleimportChats()}
            >
                {i18n.t("contacts.confirmationModal.wantImport")}
            </ConfirmationModal>

            <MainHeader>
                <Title>{i18n.t("contacts.title")} {loading && contacts.length === 0 ? "" : `(${contacts.length})`}</Title>
                <MainHeaderButtonsWrapper>
                    <TagsFilter
                        onFiltered={handleSelectedTags}
                    />
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <PopupState variant="popover" popupId="demo-popup-menu">
                        {(popupState) => (
                            <React.Fragment>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    {...bindTrigger(popupState)}
                                    style={{ color: 'white' }}
                                >
                                    Importar / Exportar
                                    <ArrowDropDown />
                                </Button>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem
                                        onClick={() => {
                                            setConfirmOpen(true);
                                            setImportContacts(true);
                                            popupState.close();
                                        }}
                                    >
                                        <ContactPhone
                                            fontSize="small"
                                            color="primary"
                                            style={{
                                                marginRight: 10,
                                            }}
                                        />
                                        {i18n.t("contacts.menu.importYourPhone")}
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => { setImportContactModalOpen(true) }}

                                    >
                                        <Backup
                                            fontSize="small"
                                            color="primary"
                                            style={{
                                                marginRight: 10,
                                            }}
                                        />
                                        {i18n.t("contacts.menu.importToExcel")}

                                    </MenuItem>
                                </Menu>
                            </React.Fragment>
                        )}
                    </PopupState>

                    {/* BOTÃO DE DELETAR SELECIONADOS PADRONIZADO COM A COR DO WHITELABEL E TEXTO BRANCO */}
					<Badge
						badgeContent={duplicateCandidatesCount}
						color="error"
						overlap="rectangular"
						style={{ marginRight: 8 }}
						invisible={duplicateCandidatesCount === 0}
					>
						<Button
							variant="outlined"
							color="secondary"
							onClick={() => setDuplicatesModalOpen(true)}
						>
							Possíveis Duplicados
						</Button>
					</Badge>

					<Button
						variant="contained"
						onClick={() => setConfirmDeleteManyOpen(true)}
						disabled={selectedContactIds.length === 0 || loading}
						style={{
							marginRight: 8,
							backgroundColor: theme.palette.primary.main, // Utiliza a cor primária do tema
							color: 'white' // Adiciona a cor do texto como branco
						}}
					>
						Deletar Selecionados ({selectedContactIds.length})
					</Button>

					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenContactModal}
						style={{ color: 'white' }}
					>
						{i18n.t("contacts.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

            {importContactModalOpen && (
                <ContactImportWpModal
                    isOpen={importContactModalOpen}
                    handleClose={() => setImportContactModalOpen(false)}
                    selectedTags={selectedTags}
                    hideNum={hideNum}
                    userProfile={user.profile}
                />
            )}
            <Paper
                className={classes.mainPaper}
                variant="outlined"
                onScroll={handleScroll}
            >
                <>
                    <input
                        style={{ display: "none" }}
                        id="upload"
                        name="file"
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={() => {
                            setConfirmOpen(true);
                        }}
                        ref={fileUploadRef}
                    />
                </>
                {isMobile ? (
                    <Box p={1}>
                        {orgGroups.map(({ org, contacts: orgContacts }) => renderMobileOrgCard(org, orgContacts))}
                        {ungroupedContacts.length > 0 && orgGroups.length > 0 && (
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ display: "block", fontWeight: 700, margin: theme.spacing(1.5, 0, 0.5) }}
                            >
                                SEM EMPRESA VINCULADA
                            </Typography>
                        )}
                        {ungroupedContacts.length > 0 && (
                            <Paper variant="outlined">
                                {ungroupedContacts.map((contact) => renderMobileContactLine(contact))}
                            </Paper>
                        )}
                        {loading && (
                            <Box textAlign="center" py={2}>
                                <Typography variant="caption" color="textSecondary">Carregando...</Typography>
                            </Box>
                        )}
                        {!loading && orgGroups.length === 0 && ungroupedContacts.length === 0 && (
                            <Box textAlign="center" py={4}>
                                <Typography variant="body2" color="textSecondary">Nenhum contato encontrado.</Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {/* NOVO CHECKBOX PARA SELECIONAR TUDO */}
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={isSelectAllChecked}
                                    onChange={handleSelectAllContacts}
                                    inputProps={{ "aria-label": "Selecionar todos os contatos" }}
                                />
                            </TableCell>
                            <TableCell style={{ paddingRight: 0 }} /> {/* Coluna para Avatar */}
                            <TableCell>
                                {i18n.t("contacts.table.name")}
                            </TableCell>
                            <TableCell align="center">
                                {i18n.t("contacts.table.whatsapp")}
                            </TableCell>
                            <TableCell align="center">
                                {i18n.t("contacts.table.email")}
                            </TableCell>
                            <TableCell align="center">{"Canal"}</TableCell>
                            <TableCell align="center">{"CNPJ/CPF"}</TableCell>
                            <TableCell align="center">{"Status"}</TableCell>
                            <TableCell align="center">
                                {i18n.t("contacts.table.actions")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {orgGroups.map(({ org, contacts: orgContacts }) => {
                                const isExpanded = expandedOrgIds.has(org.id);
                                return (
                                    <React.Fragment key={`org-${org.id}`}>
                                        <TableRow
                                            hover
                                            style={{ cursor: "pointer", background: theme.palette.action.hover }}
                                            onClick={() => toggleOrgExpanded(org.id)}
                                        >
                                            <TableCell padding="checkbox" />
                                            <TableCell style={{ paddingRight: 0 }}>
                                                <IconButton size="small">
                                                    {isExpanded ? (
                                                        <ExpandLessIcon fontSize="small" />
                                                    ) : (
                                                        <ExpandMoreIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="flex-start" gridGap={6}>
                                                    <BusinessIcon fontSize="small" color="disabled" style={{ marginTop: 2 }} />
                                                    <Box>
                                                        <b>{org.corporateName || org.tradeName || "(sem razão social)"}</b>
                                                        {org.tradeName && org.tradeName !== org.corporateName && (
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                {org.tradeName}
                                                            </Typography>
                                                        )}
                                                        {renderOrgContracts(org)}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                {orgContacts.length} {orgContacts.length === 1 ? "telefone" : "telefones"}
                                            </TableCell>
                                            <TableCell align="center">—</TableCell>
                                            <TableCell align="center">—</TableCell>
                                            <TableCell align="center">{formatCnpjCpf(org.documentNumber)}</TableCell>
                                            <TableCell align="center">—</TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" onClick={(e) => handleOpenLinkMenu(e, org)} title="Adicionar contato a esta empresa">
                                                    <AddCircleOutlineIcon fontSize="small" color="primary" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        {isExpanded &&
                                            orgContacts.map((contact) => (
                                                <TableRow key={contact.id} style={{ background: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#fcfcfc" }}>
                                                    {/* NOVO CHECKBOX INDIVIDUAL */}
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedContactIds.includes(contact.id)}
                                            onChange={handleToggleSelectContact(contact.id)}
                                            inputProps={{ "aria-label": `Selecionar contato ${contact.name}` }}
                                        />
                                    </TableCell>
                                    <TableCell style={{ paddingRight: 0 }}>
                                        {<Avatar src={`${contact?.urlPicture}`} />}
                                    </TableCell>
                                    <TableCell>
                                        {isUnidentifiedContactName(contact.name) ? (
                                            <span style={{ fontStyle: "italic", color: "#9e9e9e" }} title={contact.name}>
                                                Sem identificação
                                            </span>
                                        ) : contact.name}
                                    </TableCell>
                                    <TableCell align="center">
                                        {((enableLGPD && hideNum && user.profile === "user")
                                            ? contact.isGroup
                                                ? contact.number :
                                                formatSerializedId(contact?.number) === null ? contact.number.slice(0, -6) + "**-**" + contact?.number.slice(-2) :
                                                    formatSerializedId(contact?.number)?.slice(0, -6) + "**-**" + contact?.number?.slice(-2) :
                                                    contact.isGroup ? contact.number : formatSerializedId(contact?.number)
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {contact.email}
                                    </TableCell>
                                    <TableCell>{contact?.whatsapp?.name}</TableCell>
                                    <TableCell align="center">
                                        {contact?.contactOrganization?.documentNumber || "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {contact.active ? (
                                            <CheckCircleIcon
                                                style={{ color: "green" }}
                                                fontSize="small"
                                            />
                                        ) : (
                                            <CancelIcon
                                                style={{ color: "red" }}
                                                fontSize="small"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            disabled={!contact.active}
                                            onClick={() => {
                                                setContactTicket(contact);
                                                setNewTicketModalOpen(true);
                                            }}
                                        >
                                            {contact.channel === "whatsapp" && (<WhatsApp style={{ color: "green" }} />)}
                                            {contact.channel === "instagram" && (<Instagram style={{ color: "purple" }} />)}
                                            {contact.channel === "facebook" && (<Facebook style={{ color: "blue" }} />)}
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                hadleEditContact(contact.id)
                                            }
                                        >
                                            <EditIcon color="secondary" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={
                                                contact.active
                                                    ? () => {
                                                        setConfirmOpen(true);
                                                        setBlockingContact(
                                                            contact
                                                        );
                                                    }
                                                    : () => {
                                                        setConfirmOpen(true);
                                                        setUnBlockingContact(
                                                            contact
                                                        );
                                                    }
                                            }
                                        >
                                            {contact.active ? (
                                                <BlockIcon color="secondary" />
                                            ) : (
                                                <CheckCircleIcon color="secondary" />
                                            )}
                                        </IconButton>
                                        <Can
                                            role={user.profile}
                                            perform="contacts-page:deleteContact"
                                            yes={() => (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        setConfirmOpen(true);
                                                        setDeletingContact(
                                                            contact
                                                        );
                                                    }}
                                                >
                                                    <DeleteOutlineIcon color="secondary" />
                                                </IconButton>
                                            )}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                                    </React.Fragment>
                                );
                            })}
                            {ungroupedContacts.length > 0 && orgGroups.length > 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={9}
                                        style={{ background: theme.palette.action.hover, fontWeight: "bold", fontSize: "0.75em", color: theme.palette.text.secondary }}
                                    >
                                        SEM EMPRESA VINCULADA
                                    </TableCell>
                                </TableRow>
                            )}
                            {ungroupedContacts.map((contact) => (
                                <TableRow key={contact.id}>
                                    {/* NOVO CHECKBOX INDIVIDUAL */}
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedContactIds.includes(contact.id)}
                                            onChange={handleToggleSelectContact(contact.id)}
                                            inputProps={{ "aria-label": `Selecionar contato ${contact.name}` }}
                                        />
                                    </TableCell>
                                    <TableCell style={{ paddingRight: 0 }}>
                                        {<Avatar src={`${contact?.urlPicture}`} />}
                                    </TableCell>
                                    <TableCell>
                                        {isUnidentifiedContactName(contact.name) ? (
                                            <span style={{ fontStyle: "italic", color: "#9e9e9e" }} title={contact.name}>
                                                Sem identificação
                                            </span>
                                        ) : contact.name}
                                    </TableCell>
                                    <TableCell align="center">
                                        {((enableLGPD && hideNum && user.profile === "user")
                                            ? contact.isGroup
                                                ? contact.number :
                                                formatSerializedId(contact?.number) === null ? contact.number.slice(0, -6) + "**-**" + contact?.number.slice(-2) :
                                                    formatSerializedId(contact?.number)?.slice(0, -6) + "**-**" + contact?.number?.slice(-2) :
                                                    contact.isGroup ? contact.number : formatSerializedId(contact?.number)
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {contact.email}
                                    </TableCell>
                                    <TableCell>{contact?.whatsapp?.name}</TableCell>
                                    <TableCell align="center">
                                        {contact?.contactOrganization?.documentNumber || "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {contact.active ? (
                                            <CheckCircleIcon
                                                style={{ color: "green" }}
                                                fontSize="small"
                                            />
                                        ) : (
                                            <CancelIcon
                                                style={{ color: "red" }}
                                                fontSize="small"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            disabled={!contact.active}
                                            onClick={() => {
                                                setContactTicket(contact);
                                                setNewTicketModalOpen(true);
                                            }}
                                        >
                                            {contact.channel === "whatsapp" && (<WhatsApp style={{ color: "green" }} />)}
                                            {contact.channel === "instagram" && (<Instagram style={{ color: "purple" }} />)}
                                            {contact.channel === "facebook" && (<Facebook style={{ color: "blue" }} />)}
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                hadleEditContact(contact.id)
                                            }
                                        >
                                            <EditIcon color="secondary" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={
                                                contact.active
                                                    ? () => {
                                                        setConfirmOpen(true);
                                                        setBlockingContact(
                                                            contact
                                                        );
                                                    }
                                                    : () => {
                                                        setConfirmOpen(true);
                                                        setUnBlockingContact(
                                                            contact
                                                        );
                                                    }
                                            }
                                        >
                                            {contact.active ? (
                                                <BlockIcon color="secondary" />
                                            ) : (
                                                <CheckCircleIcon color="secondary" />
                                            )}
                                        </IconButton>
                                        <Can
                                            role={user.profile}
                                            perform="contacts-page:deleteContact"
                                            yes={() => (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        setConfirmOpen(true);
                                                        setDeletingContact(
                                                            contact
                                                        );
                                                    }}
                                                >
                                                    <DeleteOutlineIcon color="secondary" />
                                                </IconButton>
                                            )}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton avatar columns={6} />}
                        </>
                    </TableBody>
                </Table>
                )}
            </Paper>
        </MainContainer >
    );
};

export default Contacts;